import mixpanel from '@disco/api/mixpanel'
import { CUSTOM_CHANNEL, INBOX_TYPES } from '@disco/constants/GroupTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import { MAIN } from '@disco/constants/Nesting'
import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'
import {
  SIMILARITY_FEEDBACK_NEGATIVE,
  SIMILARITY_FEEDBACK_POSITIVE,
} from '@disco/constants/SimilarityFeedbackTypes'
import {
  addAggregatedTrackToDisco,
  removeTrackFromGroup,
  removeTracksFromPlaylist,
  sendSimilarityFeedback,
  toggleSharedChannelTrackInLibrary,
  toggleTrackInLibrary,
} from '@disco/state-management/actions/ExplorerActions'
import { resetSelection } from '@disco/state-management/actions/SelectionActions'
import { deleteTrack } from '@disco/state-management/actions/ServerActions'
import {
  getSelectedLibrary,
  isDeviceTypePhone,
} from '@disco/state-management/selectors/app'
import { getGroups } from '@disco/state-management/selectors/groups'
import { getNestingLabel } from '@disco/state-management/selectors/nesting'
import { getSelectionIndexes } from '@disco/state-management/selectors/selection'
import { getUser } from '@disco/state-management/selectors/user'
import { sendPlaylistDownloadEventThrottled } from '@disco/utils/eventsApi'
import {
  getFolderChannel,
  isChannelShared,
  itemRequireAddingToTeamLib,
} from '@disco/utils/groups'
import { getRowIconMixpanelContext } from '@disco/utils/mixpanel'
import { getNestedTracksCount, isReadOnlyTrack } from '@disco/utils/tracks'

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate } from 'react-router-dom'
import type { TrackTextSettings } from 'types/business'
import type { ExplorerPane } from 'types/explorer'
import type { Group } from 'types/group'
import type { State } from 'types/redux'
import type { Track } from 'types/track'
import { NestingVersionsContext } from 'contexts/nesting-versions'
import { SelectionContext } from 'contexts/selection'
import ActionStar from 'components/common/action-star'
import ActionThumb from 'components/common/action-thumb'
import TrackItem from 'components/common/track-item'
import PlaylistTrackVersions from 'components/explorer/playlist/track-versions'
import TrackVersionsButton from 'components/nesting/track-versions-button'
import TrackActions from 'components/track-actions'
import TrackTooltip from 'components/track-tooltip'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Draggable from 'components/ui/draggable'
import Flex from 'components/ui/flex'
import { HoverProvider } from 'components/ui/hover-provider'
import Icon from 'components/ui/icon'
import Lozenge from 'components/ui/lozenge'
import type { PaneItemAppearance } from 'components/ui/pane-item'
import getTrackStatus from 'components/utils/track-status'
type User = {
  groups: Group[]
  settings: {
    hide_inbox_removal_warning?: boolean
  }
}
export type ExplorerTrackProps = {
  appearance: PaneItemAppearance
  // TODO: add selection feature for track panel with versions.
  disableSelection?: boolean
  isSimilarity?: boolean
  pane: ExplorerPane
  versionsButton?: React.ReactNode
  sourceGroupId?: number | null | undefined
  sourceGroupType?: string | null | undefined
}
type Props = ExplorerTrackProps & {
  onClick?: (event: MouseEvent) => void
  index: number
  showTrackVersions?: boolean
  sourcePlaylistId: number | null | undefined
  track: Track
  onShowTrackVersions?: (open: boolean) => void
  textSettings?: TrackTextSettings
}

const ExplorerTrack = ({
  appearance,
  disableSelection,
  index,
  isSimilarity,
  pane,
  showTrackVersions = false,
  sourcePlaylistId,
  track,
  versionsButton: propVersionsButton,
  onShowTrackVersions,
  onClick,
  sourceGroupId,
  sourceGroupType,
  textSettings,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const selectionContext = useContext(SelectionContext)
  const nestingVersionsContext = useContext(NestingVersionsContext)
  const groups: Group[] = useSelector(getGroups)
  const library: Group | null | undefined = useSelector(getSelectedLibrary)
  const selection: Record<string, any> = useSelector((state: State) =>
    getSelectionIndexes(state, selectionContext.id),
  )
  const isPartOfMultipleSelection: boolean =
    !disableSelection && selection.includes(index)
  const isPhone = useSelector(isDeviceTypePhone)
  const readOnlyTrack = isReadOnlyTrack(track)
  const user = useSelector(getUser)
  const isStarred: boolean = useMemo(() => {
    if (!user) return false
    const starredUrl = user.groups[1]?.url
    return starredUrl ? track.groups.includes(starredUrl) : false
  }, [track, user])
  const mixpanelContext = isSimilarity
    ? 'Similar tracks'
    : pane.type === SEARCH_RESULTS_PANE && !pane.groupId
    ? 'Search results'
    : getRowIconMixpanelContext(library)
  const handleTrackClick = useCallback(
    (event: MouseEvent) => {
      if (onClick) {
        onClick(event)
      }
    },
    [onClick],
  )
  const handleFavoriteClick = useCallback(() => {
    if (readOnlyTrack) {
      mixpanel.sendPureEvent('Click icon', {
        'Track ID': track.id,
        Type: 'Track',
        Icon: 'Star',
        Context: mixpanelContext,
        Action: 'Import',
        Catalog: track.origin?.source_library?.id,
        'Catalog Business': track.origin?.business?.id,
      })
      return dispatch(
        addAggregatedTrackToDisco({ track, skipFavoriteEvent: true }),
      )
    }

    if (library && isChannelShared(library)) {
      dispatch(toggleSharedChannelTrackInLibrary(track, library))
    } else {
      dispatch(toggleTrackInLibrary(track))
    }

    mixpanel.sendPureEvent('Click icon', {
      'Track ID': track.id,
      Type: 'Track',
      Icon: 'Star',
      Context: mixpanelContext,
      Action: isStarred ? 'Unstar' : 'Star',
    })

    if (
      library &&
      INBOX_TYPES.includes(library.group_type) &&
      !isStarred &&
      pane.playlist_id
    ) {
      sendPlaylistDownloadEventThrottled(
        pane.playlist_id,
        undefined,
        library.id,
      )
    }
  }, [
    dispatch,
    isStarred,
    library,
    mixpanelContext,
    pane,
    track,
    readOnlyTrack,
  ])
  const handleDelete = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.stopPropagation()

      if (user.settings && user.settings.hide_inbox_removal_warning) {
        dispatch(
          deleteTrack({
            id: track.id,
          }),
        )
        mixpanel.sendPureEvent('Click icon', {
          'Track ID': track.id,
          Type: 'Track',
          Icon: 'Delete',
          Context: mixpanelContext,
        })
      } else if (library) {
        navigate(`/inboxes/${library.id}/tracks/${track.id}/remove`)
      }
    },
    [dispatch, library, mixpanelContext, track, user],
  )
  const handleGroupRemoveClick = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.stopPropagation()
      const targetGroup: Group | null | undefined = groups.find(
        (group: Group) => group.id === pane.groupId,
      )

      if (targetGroup && track) {
        dispatch(removeTrackFromGroup(track, targetGroup))
        mixpanel.sendPureEvent('Click icon', {
          'Track ID': track.id,
          Type: 'Track',
          Icon: 'Delete',
          Context: mixpanelContext,
        })
      }
    },
    [dispatch, groups, library, mixpanelContext, pane, track],
  )
  const handleRemoveClick = useCallback(
    (event: React.SyntheticEvent<any>) => {
      event.stopPropagation()
      // @ts-expect-error
      dispatch(removeTracksFromPlaylist(pane, [track]))
      mixpanel.sendPureEvent('Click icon', {
        'Track ID': track.id,
        Type: 'Track',
        Icon: 'Delete',
        Context: mixpanelContext,
      })
    },
    [dispatch, mixpanelContext, pane, track],
  )
  const handleSimilarityFeedbackClick = useCallback(
    feedback => {
      dispatch(
        sendSimilarityFeedback(
          track.id,
          feedback,
          pane.id,
          pane.simSearch?.url,
        ),
      )
    },
    [dispatch, track.id, pane.id, pane.simSearch?.url],
  )
  const handleDragStart = useCallback(() => {
    dispatch(resetSelection())
  }, [dispatch])
  const [open, setOpen] = useState(showTrackVersions)
  useEffect(() => {
    setOpen(showTrackVersions)
  }, [showTrackVersions])
  const versionsCount = getNestedTracksCount(track)
  const [deleteTitle, deleteFn] = useMemo(() => {
    if (library) {
      const parentGroup = getFolderChannel(groups, library)

      if (parentGroup?.lock_content) {
        return ['', null]
      }

      if (library.group_type === CUSTOM_CHANNEL && pane.groupId) {
        return ['Remove from channel', handleGroupRemoveClick]
      }

      if (INBOX_TYPES.includes(library.group_type) && pane.index === 1) {
        if (track.groups && track.groups.includes(library.url)) {
          if (!itemRequireAddingToTeamLib(track.groups, groups)) {
            return ['Remove from inbox', handleRemoveClick]
          } else {
            return ['Remove from DISCO', handleDelete]
          }
        }
      }
    }

    return ['', null]
  }, [
    groups,
    handleDelete,
    handleGroupRemoveClick,
    handleRemoveClick,
    library,
    pane,
    track,
  ])
  const { isTemp } = getTrackStatus(track)
  let customEditUrl: string | null | undefined = null

  if (
    typeof index === 'number' &&
    sourcePlaylistId &&
    !nestingVersionsContext.nesting
  ) {
    customEditUrl = `/explorer/playlists/${sourcePlaylistId}/tracks/${index}/edit-metadata`
  }

  const playbackContext = {
    groupId: library?.id,
    paneIndex: pane?.index,
    sourcePlaylistId,
  }
  const actionThumbItems = [
    {
      renderIcon: () => (
        <Icon width="24" height="24" type="thumbs-up" color="#5f63ee" />
      ),
      value: 'similar',
      label: 'Similar',
      onClick: () => {
        handleSimilarityFeedbackClick(SIMILARITY_FEEDBACK_POSITIVE)
      },
    },
    {
      renderIcon: () => (
        <Icon
          width="24"
          height="24"
          type="thumbs-up"
          color="#FD7D69"
          style={{
            transform: 'rotate(180deg)',
          }}
        />
      ),
      value: 'not_similar',
      label: 'Not similar',
      onClick: () => {
        handleSimilarityFeedbackClick(SIMILARITY_FEEDBACK_NEGATIVE)
      },
    },
  ]
  let nodeRight: React.ReactNode = null
  const nestingLabel = getNestingLabel(track)
  const showVersions = pane.type === 'playlist' && nestingLabel === MAIN

  if (nestingLabel) {
    nodeRight = <Lozenge color="gray" text={nestingLabel} bold />
  }

  let versionsButton = propVersionsButton

  if (showVersions && versionsCount > 0) {
    versionsButton = (
      <TrackVersionsButton
        open={open}
        count={versionsCount}
        onClick={() => {
          setOpen(!open)

          if (onShowTrackVersions) {
            onShowTrackVersions(!open)
          }
        }}
      />
    )
    nodeRight = (
      <div className="ShortlistVersionsIndicator">{versionsButton}</div>
    )
  }

  if (propVersionsButton) {
    nodeRight = (
      <div>
        <Flex spacing="none">
          <TrackActions
            buttonSize="compact"
            customEditMetadataUrl={customEditUrl}
            group={library}
            groupId={library?.id}
            sourcePlaylistId={sourcePlaylistId}
            track={track}
            addToCustomGroup
            addToNewCustomGroup
            addToShortlist={isPhone}
            allowSharing
            deleteTrack={true}
            downloadTrack
            editMetadata
            manageNesting
            manageTrackFormats
            permissionsAndSharing
            copyMetadata
            versionsButton={propVersionsButton}
            hideEls={['native_tags', 'notes_shortcut']}
            pane={pane}
            mixpanelContext={mixpanelContext}
          />
        </Flex>
      </div>
    )
  }

  return (
    <>
      <Draggable
        id={`${pane.index}-track-${track.id}`}
        data={{
          isPartOfMultipleSelection,
          selection,
          sourcePlaylistId,
          track,
          sourceGroupId,
          sourceGroupType,
        }}
        type={TRACK}
        onDragStart={isPartOfMultipleSelection ? undefined : handleDragStart}
      >
        <HoverProvider enableTouchSupport>
          <TrackItem // TODO: add selection feature for track panel with versions.
            appearance={
              appearance === 'selected' && disableSelection
                ? 'default'
                : appearance
            }
            track={track}
            textSettings={textSettings} // TODO: move `location` to context
            location="explorer"
            hideWatermarkingFlag={Boolean(pane?.playlist_id)}
            itemIndex={index}
            actions={
              <ButtonContainer>
                {!isPhone && (
                  <ButtonContainer>
                    {isSimilarity && <ActionThumb items={actionThumbItems} />}
                    {
                      <TrackTooltip
                        customEditMetadataUrl={customEditUrl}
                        showViewLink
                        track={track}
                      />
                    }
                    {track.permissions?.manage && !isTemp && (
                      <ActionStar
                        active={isStarred}
                        onClick={handleFavoriteClick}
                        actionKind={readOnlyTrack ? 'import' : 'star'}
                      />
                    )}
                    {/* allow removal only for dropbox file uploads for now */}
                    {track?.permissions?.delete &&
                      (!isTemp || track.file?.link) &&
                      deleteFn && (
                        <Button
                          appearance="icon"
                          compact
                          icon="clear"
                          tooltip={deleteTitle}
                          onClick={deleteFn}
                        />
                      )}
                  </ButtonContainer>
                )}
                <TrackActions
                  buttonSize="compact"
                  customEditMetadataUrl={customEditUrl}
                  group={library}
                  groupId={library?.id}
                  sourcePlaylistId={sourcePlaylistId}
                  track={track}
                  addToCustomGroup
                  addToNewCustomGroup
                  addToShortlist={isPhone}
                  allowSharing
                  deleteTrack={true}
                  downloadTrack
                  editMetadata
                  manageNesting
                  manageTrackFormats
                  permissionsAndSharing
                  copyMetadata
                  versionsButton={versionsButton}
                  hideEls={['notes_shortcut']}
                  pane={pane}
                  mixpanelContext={mixpanelContext}
                />
              </ButtonContainer>
            }
            nodeRight={nodeRight}
            onClick={handleTrackClick}
            {...playbackContext}
          />
        </HoverProvider>
      </Draggable>
      {showVersions && open && (
        <PlaylistTrackVersions mainTrack={track} currentTrackIndex={index} />
      )}
    </>
  )
}

export default ExplorerTrack
